import { useRef } from 'react';
import MuiPopper, { PopperProps } from '@mui/material/Popper';
import MUIPaper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { styled } from '../styles';
import Hyperlink, { Props as HyperlinkProps } from '../Hyperlink';

export interface Props extends Omit<PopperProps, 'open'> {
  isOpen: boolean;
  content?: string;
  actionProps?: HyperlinkProps;
}

const Popper = styled(MuiPopper)(({ theme }) => ({
  '.MuiPaper-root': {
    boxShadow: '0px 5px 15px 3px rgba(0, 0, 0, 0.1)',
  },
  zIndex: 1,
  '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
    top: 0,
    left: 0,
    marginTop: '-9px',
    width: '30px',
    height: '10px',
    '&::before': {
      borderWidth: '0 10px 10px 10px',
      borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
      filter: 'drop-shadow(0px -4px 4px rgba(0, 0, 0, 0.07))',
    },
  },
  '&[data-popper-placement*="top"] .MuiPopper-arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-9px',
    width: '30px',
    height: '10px',
    '&::before': {
      borderWidth: '10px 10px 0 10px',
      borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
      filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.07))',
    },
  },
  '&[data-popper-placement*="right"] .MuiPopper-arrow': {
    left: 0,
    marginLeft: '-9px',
    height: '30px',
    width: '10px',
    '&::before': {
      borderWidth: '10px 10px 10px 0',
      borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
      filter: 'drop-shadow(-4px 0px 4px rgba(0, 0, 0, 0.07))',
    },
  },
  '&[data-popper-placement*="left"] .MuiPopper-arrow': {
    right: 0,
    marginRight: '-9px',
    height: '30px',
    width: '10px',
    '&::before': {
      borderWidth: '10px 0 10px 10px',
      borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
      filter: 'drop-shadow(4px 0px 4px rgba(0, 0, 0, 0.07))',
    },
  },
}));

const Arrow = styled('div')({
  position: 'absolute',
  fontSize: 7,
  width: '3em',
  height: '3em',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  },
});

const Container = styled(MUIPaper)(({ theme: { spacing } }) => ({
  padding: spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(2),
  borderRadius: '12px',
  maxWidth: '312px',
}));

const TooltipRich = ({ placement, isOpen, anchorEl, actionProps, content }: Props) => {
  const arrowRef = useRef(null);
  const popperSkidding = 0;
  const popperDistance = 16;

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [popperSkidding, popperDistance],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 16,
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            element: arrowRef.current,
          },
        },
      ]}
    >
      <div>
        <Arrow ref={arrowRef} className="MuiPopper-arrow" />
        <Container>
          <Typography>{content}</Typography>
          {actionProps && <Hyperlink variant="large" {...actionProps} />}
        </Container>
      </div>
    </Popper>
  );
};

export default TooltipRich;
